import jsPDF from 'jspdf';
import logoText from '../assets/images/logo.png';

// Utility to format date as dd/mm/yyyy
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
};

// Utility to format currency
const formatCurrency = (amount) => {
  const safeAmount = Number(amount) || 0;
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(safeAmount);
};

export default function printProfitPDF(data) {
  const {
    overViewDate,
    totalPatientPay,
    totalProfit,
    expenses,
    selectedCategory,
    allInputs,
    allWithdraws,
    currentlyInCachier,
    arkaMengjesit = 0,
    patients = [],
    isDayFilter = false
  } = data;

  const { allExpenses, rangedExpenses } = expenses || {};

  // Use the currentlyInCachier value passed from PaymentsReview component
  const clinicBalance = currentlyInCachier || 0;
  const today = formatDate(new Date());

  // Initialize PDF
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: [210, 297],
    compress: true
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // Margins and Brand Colors
  const leftMargin = 15;
  const rightMargin = 15;
  const contentWidth = pageWidth - leftMargin - rightMargin;
  
  const brandPrimary = '#0f766e'; // Deep Teal
  const brandSecondary = '#f0fdfa'; // Light Teal Background
  const textDark = '#1e293b'; // Slate 800
  const textMuted = '#64748b'; // Slate 500
  const borderColor = '#cbd5e1'; // Slate 300
  
  const footerY = pageHeight - 20;

  // Set white background
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');

  // --- HEADER SECTION ---
  doc.addImage(logoText, 'PNG', leftMargin, 12, 18, 18);

  doc.setTextColor(brandPrimary);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('Dental Med DIP SH.P.K.', pageWidth / 2, 20, { align: 'center' });
  
  doc.setTextColor(textMuted);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text('Raporti Financiar', pageWidth / 2, 28, { align: 'center' });
  
  // Header Divider
  doc.setDrawColor(brandPrimary);
  doc.setLineWidth(0.5);
  doc.line(leftMargin, 38, pageWidth - rightMargin, 38);

  // --- OVERVIEW BANNER ---
  let y = 45;
  doc.setFillColor(brandSecondary);
  doc.setDrawColor(borderColor);
  doc.setLineWidth(0.2);
  doc.roundedRect(leftMargin, y, contentWidth, 20, 2, 2, 'FD');
  
  doc.setTextColor(brandPrimary);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Detajet e Raportit', leftMargin + 5, y + 8);
  
  doc.setTextColor(textDark);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const overviewText = `Periudha: ${overViewDate || 'N/A'}    |    Filtri: ${selectedCategory === 'all' ? 'Të Gjitha' : selectedCategory}`;
  doc.text(overviewText, leftMargin + 5, y + 15);
  
  y += 30;

  // --- PATIENTS TABLES ---
  function drawPatientTable(title, patientsArr, startY) {
    const tableHeaders = ['Pacienti', 'Viti', 'Vendbanimi', 'Pagesa'];
    const colWidths = [60, 25, 60, 35];
    const rowHeight = 9;
    let currentY = startY;

    // Section Title
    doc.setTextColor(textDark);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(title, leftMargin, currentY);
    currentY += 5;

    // Table Math
    const tableWidth = colWidths.reduce((a, b) => a + b, 0);
    const tableStartX = leftMargin;

    // Draw Header Row Background
    doc.setFillColor(brandPrimary);
    doc.rect(tableStartX, currentY, tableWidth, rowHeight, 'F');

    // Draw Header Text
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    
    let xOffset = tableStartX;
    tableHeaders.forEach((header, i) => {
      // Right align the currency column, left align the rest
      const align = i === 3 ? 'right' : 'left';
      const textX = i === 3 ? xOffset + colWidths[i] - 5 : xOffset + 5;
      doc.text(header, textX, currentY + 6, { align });
      xOffset += colWidths[i];
    });
    
    currentY += rowHeight;

    // Draw Rows
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    
    patientsArr.forEach((pt, idx) => {
      // Zebra striping
      if (idx % 2 === 0) {
        doc.setFillColor(248, 250, 252); // Very light gray
        doc.rect(tableStartX, currentY, tableWidth, rowHeight, 'F');
      }

      doc.setTextColor(textDark);
      let rX = tableStartX;
      
      // Name
      const nameStr = doc.splitTextToSize(String(pt.full_name || '-'), colWidths[0] - 5);
      doc.text(nameStr[0], rX + 5, currentY + 6);
      rX += colWidths[0];
      
      // DOB
      const birthYear = pt.dob && !isNaN(new Date(pt.dob).getTime()) ? new Date(pt.dob).getFullYear() : '-';
      doc.text(String(birthYear), rX + 5, currentY + 6);
      rX += colWidths[1];
      
      // Location
      const locStr = doc.splitTextToSize(String(pt.location || '-'), colWidths[2] - 5);
      doc.text(locStr[0], rX + 5, currentY + 6);
      rX += colWidths[2];
      
      // Payment (Right Aligned)
      const payment = Number(pt.patient_pay) || Number(pt.paid_amount) || 0;
      doc.text(formatCurrency(payment), rX + colWidths[3] - 5, currentY + 6, { align: 'right' });
      
      // Subtle bottom border
      doc.setDrawColor(241, 245, 249);
      doc.line(tableStartX, currentY + rowHeight, tableStartX + tableWidth, currentY + rowHeight);

      currentY += rowHeight;
    });

    return currentY + 10;
  }

  // Aggregate Patients
  const stomatologyPatients = [];
  const pulmonologyPatients = [];
  
  (patients || []).forEach(curr => {
    const isPulm = curr.category === 'pulmonology' || curr.category === 'Pulmonologji';
    const targetArr = isPulm ? pulmonologyPatients : stomatologyPatients;
    
    const existing = targetArr.find(p => p.patient_id === curr.patient_id);
    if (existing) {
      existing.patient_pay = (Number(existing.patient_pay) || 0) + (Number(curr.patient_pay) || 0);
      existing.paid_amount = (Number(existing.paid_amount) || 0) + (Number(curr.paid_amount) || 0);
    } else {
      targetArr.push({ ...curr });
    }
  });

  // Render Tables (Check pagination)
  if (stomatologyPatients.length > 0) {
    if (y > pageHeight - 60) { doc.addPage(); y = 20; }
    y = drawPatientTable('Pacientët - Stomatologji', stomatologyPatients, y);
  }
  
  if (pulmonologyPatients.length > 0) {
    if (y > pageHeight - 60) { doc.addPage(); y = 20; }
    y = drawPatientTable('Pacientët - Pulmonologji', pulmonologyPatients, y);
  }

  // --- FINANCIAL BREAKDOWN SECTION ---
  // Page break check for summaries
  if (y > pageHeight - 80) {
    doc.addPage();
    y = 20;
  }

  const boxWidth = (contentWidth / 2) - 4;
  const expensesStartX = leftMargin;
  const incomeStartX = leftMargin + boxWidth + 8;
  
  // Section Titles
  doc.setTextColor(brandPrimary);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Shpenzimet', expensesStartX, y);
  doc.text('Bilanci & Të Ardhurat', incomeStartX, y);
  y += 5;

  let expensesY = y;
  let incomeY = y;

  // 1. Draw Expenses Column
  doc.setFillColor(250, 250, 250);
  doc.setDrawColor(borderColor);
  doc.roundedRect(expensesStartX, expensesY, boxWidth, 65, 2, 2, 'FD');
  
  const categoryLabels = { 'Food': 'Ushqimi', 'Material': 'Materiali', 'Tech': 'Tekniku', 'Other': 'Tjera' };
  let expRowY = expensesY + 8;
  
  // Group Expenses
  const expGroups = { 'Food': 0, 'Material': 0, 'Tech': 0, 'Other': 0 };
  (rangedExpenses || []).forEach(e => {
    const cat = e.category || 'Other';
    if(expGroups[cat] !== undefined) expGroups[cat] += Number(e.paid_amount) || 0;
  });

  Object.entries(expGroups).forEach(([cat, amount]) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(textDark);
    doc.text(categoryLabels[cat], expensesStartX + 5, expRowY);
    doc.text(formatCurrency(amount), expensesStartX + boxWidth - 5, expRowY, { align: 'right' });
    expRowY += 8;
  });

  // Total Expenses Footer
  doc.setDrawColor(borderColor);
  doc.line(expensesStartX + 5, expRowY + 2, expensesStartX + boxWidth - 5, expRowY + 2);
  doc.setFont('helvetica', 'bold');
  doc.text('Totali', expensesStartX + 5, expRowY + 10);
  doc.setTextColor('#ef4444'); // Red for expenses
  doc.text(formatCurrency(allExpenses || 0), expensesStartX + boxWidth - 5, expRowY + 10, { align: 'right' });


  // 2. Draw Income/Balance Column
  doc.setFillColor(250, 250, 250);
  doc.setDrawColor(borderColor);
  doc.roundedRect(incomeStartX, incomeY, boxWidth, 65, 2, 2, 'FD');

  let incRowY = incomeY + 8;
  doc.setFontSize(10);

  const drawIncRow = (label, val, isBold = false, isNegative = false, isPositive = false) => {
    doc.setFont('helvetica', isBold ? 'bold' : 'normal');
    doc.setTextColor(textDark);
    doc.text(label, incomeStartX + 5, incRowY);
    
    if (isNegative) doc.setTextColor('#ef4444');
    else if (isPositive) doc.setTextColor(brandPrimary);
    
    doc.text(formatCurrency(val), incomeStartX + boxWidth - 5, incRowY, { align: 'right' });
    incRowY += 8;
  };

  // Day Filter: Transparent Ledger Math
  if (isDayFilter) {
    drawIncRow('Arka e Mëngjesit', Number(arkaMengjesit) || 0);
    drawIncRow('Pagesat nga Pacientët', Number(totalPatientPay) || 0);
    drawIncRow('Hyrjet e Tjera', Number(allInputs) || 0);
    drawIncRow('Tërheqjet', -(Number(allWithdraws) || 0), false, true);
    drawIncRow('Shpenzimet', -(Number(allExpenses) || 0), false, true);
    
    // Subtotal Line
    doc.setDrawColor(borderColor);
    doc.line(incomeStartX + 5, incRowY - 4, incomeStartX + boxWidth - 5, incRowY - 4);
    
    drawIncRow('Gjendja në Klinikë', Number(clinicBalance) || 0, true, false, true);
  } else {
    // Week/Month Filter (Standard View) – include Arka e Mëngjesit (0 when multi-day)
    drawIncRow('Arka e Mëngjesit', Number(arkaMengjesit) || 0);
    drawIncRow('Pagesat nga Pacientët', Number(totalPatientPay) || 0);
    drawIncRow('Hyrjet', Number(allInputs) || 0);
    drawIncRow('Shpenzimet', -(Number(allExpenses) || 0), false, true);
    drawIncRow('Tërheqjet', -(Number(allWithdraws) || 0), false, true);
    
    doc.setDrawColor(borderColor);
    doc.line(incomeStartX + 5, incRowY - 4, incomeStartX + boxWidth - 5, incRowY - 4);
    
    drawIncRow('Fitimi Total', Number(totalProfit) || 0, true);
  }

  // --- FOOTER ---
  doc.setDrawColor(borderColor);
  doc.setLineWidth(0.5);
  doc.line(leftMargin, footerY - 10, pageWidth - rightMargin, footerY - 10);
  
  doc.setTextColor(textMuted);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Gjakovë: ${today}`, leftMargin, footerY);
  doc.text('Dental Med DIP SH.P.K.', pageWidth - rightMargin, footerY, { align: 'right' });

  // Output
  doc.autoPrint();
  const printWindow = window.open(doc.output('bloburl'), '_blank');
  if (printWindow) {
    printWindow.document.title = `Raporti Financiar - Dental Med`;
  }
}